import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MoreIcon from '@mui/icons-material/More';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import WcIcon from '@mui/icons-material/Wc';
import { Divider, IconButton, Stack, TableCell, TableRow, Typography } from '@mui/material';
import { Qualifications } from 'features/co-pilot/campaign-builder/campaign-builder.types';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';
import PaiHelpIcon from 'features/ui/base-components/pai-help-icon';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import PaiCheckbox from 'features/ui/base-components/pai-checkbox';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiSwitch from 'features/ui/base-components/pai-switch';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useContactDrawerStore from 'stores/contact-drawer-store';
import { makeStyles } from 'tss-react/mui';
import { isUncertainSex } from '../contact-sex-utils';
import ContactItemMenu from './contact-item-menu';
import NotYetVisitedDialog from './not-yet-visited-dialog';

const DataNotAvailable = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  return (
    <Stack direction={'row'} alignItems={'center'} gap={1}>
      <Typography className={classes.notAvailableText}>{t('dataNotAvailable')}</Typography>
    </Stack>
  );
};

const NotYetVisited: React.FC<{ onOpenReasons: (event: React.MouseEvent) => void }> = ({ onOpenReasons }) => {
  const { t } = useTranslation();
  return <PaiLink text={t('notYetVisited')} size={'md'} underline={'always'} variant={'primary'} action={onOpenReasons} />;
};

const Highlight: React.FC<{ text: string; searchTerm: string }> = ({ text, searchTerm }) => {
  if (!searchTerm.trim()) return <>{text}</>;

  const regex = new RegExp(`(${_.escapeRegExp(searchTerm.trim())})`, 'gi');
  const parts = text.split(regex);

  return (
    <span>
      {parts.map((part, index) => (regex.test(part) ? <mark key={index}>{part}</mark> : <React.Fragment key={index}>{part}</React.Fragment>))}
    </span>
  );
};

const CollapsibleText: React.FC<{ text: string; maxLength: number; searchTerm?: string }> = ({ text, maxLength = 100, searchTerm = '' }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleExpand = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    setIsExpanded(!isExpanded);
  };
  const displayedText = isExpanded ? text : text.substring(0, maxLength) + (text.length > maxLength ? '...' : '');
  return (
    <div>
      {searchTerm ? <Highlight text={displayedText} searchTerm={searchTerm} /> : displayedText}
      {text.length > maxLength && (
        <PaiLink
          text={isExpanded ? t('less') : t('more')}
          inline={!isExpanded}
          ml={!isExpanded ? '4px' : undefined}
          size={'md'}
          variant={'primary'}
          underline={'hover'}
          action={toggleExpand}
        />
      )}
    </div>
  );
};

interface Props {
  funnelId: string;
  contact: FunnelContact;
  disabled: boolean;
  contactsLoading: boolean;
  contactsSearchParam: string;
  columnOrder?: string[];

  deleteMultipleContacts: boolean;
  isSelectedForDeletion: boolean;

  getVisibleColumns(): {
    aiJudgement: boolean;
    reason: boolean;
    email: boolean;
    searchName: boolean;
    customField: boolean;
  };

  getLocalizedText(key: string): string;

  handleSingleContactIsActiveChange(contact: FunnelContact): () => void;

  handleOpenContactDrawer(linkedInURL: string | undefined, profileId: string | undefined, campaignId: string | undefined): () => void;

  handleOpenDeleteContactPermanentlyDialog(contact: FunnelContact): () => void;

  handleOpenChangeAiJudgementDialog(contact: FunnelContact): () => void;

  handleRemoveContactFromBlacklist(linkedInURL: string): () => void;

  handleRemoveContactFromSuspended(linkedInURL: string): () => void;

  handleOpenUpdateSexDialog(contact: FunnelContact): () => void;

  handleRecoverContactFromDeleted(linkedInURL: string): () => void;

  handleContactsToDeleteChange(contact: FunnelContact): (event: React.MouseEvent<HTMLInputElement>) => void;
}

const ContactItem: React.FC<Props> = ({
  funnelId,
  contact,
  disabled,
  contactsLoading,
  contactsSearchParam,
  columnOrder,
  deleteMultipleContacts,
  isSelectedForDeletion,
  getVisibleColumns,
  getLocalizedText,
  handleSingleContactIsActiveChange,
  handleOpenContactDrawer,
  handleOpenDeleteContactPermanentlyDialog,
  handleOpenChangeAiJudgementDialog,
  handleRemoveContactFromBlacklist,
  handleRemoveContactFromSuspended,
  handleOpenUpdateSexDialog,
  handleRecoverContactFromDeleted,
  handleContactsToDeleteChange,
}) => {
  const { classes, cx } = useStyles();
  const { t, i18n } = useTranslation();
  const activeDrawerContact = useContactDrawerStore((state) => state.activeDrawerContact);
  const activeInDrawer = activeDrawerContact === contact.linkedInURL;
  const readableLinkedInURL = contact?.profileIds?.vanity ? `https://www.linkedin.com/in/${contact.profileIds.vanity}/` : contact.linkedInURL;
  const hasUncertainSex = isUncertainSex(contact.sex);

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notVisitedDialogOpen, setNotVisitedDialogOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  const handleOpenNotVisitedDialog = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setNotVisitedDialogOpen(true);
  };

  const handleCloseNotVisitedDialog = () => setNotVisitedDialogOpen(false);

  const visibleColumns = getVisibleColumns();

  // Default column order if not provided
  const defaultColumnOrder = [
    'isActive',
    'name',
    'aiJudgement',
    'company',
    'jobTitle',
    'location',
    'email',
    'searchName',
    'customField',
    'linkedInURL',
  ];

  const finalColumnOrder = columnOrder || defaultColumnOrder;

  // Filter visible columns based on current order
  const visibleColumnOrder = finalColumnOrder.filter((colId) => {
    if (colId === 'aiJudgement') return visibleColumns.aiJudgement;
    if (colId === 'email') return visibleColumns.email;
    if (colId === 'searchName') return visibleColumns.searchName;
    if (colId === 'customField') return visibleColumns.customField;
    return true; // Always show other columns
  });

  const mapAiJudgementToText = (judgement: string | undefined) => {
    switch (judgement) {
      case Qualifications.A:
        return getLocalizedText('topCandidates');
      case Qualifications.B:
        return getLocalizedText('strongContenders');
      case Qualifications.C:
        return getLocalizedText('backupCandidatePool');
      case Qualifications.ERROR:
        return t('aiJudgingError');
      default:
        return '-';
    }
  };

  /**
   * Why a candidate is held back by the double-contact guard, in one sentence.
   *
   * Toni reported two grade-A candidates (score 90) that stayed inactive and read it as a
   * ranking failure — 7b1c596f, then again as 40db0ad9 after we closed the first one. The
   * ranking was right and so was the guard: both had already been approached from another
   * campaign. Nothing on screen said so. This is that sentence.
   */
  const contactedElsewhereLabel = () => {
    const campaignName = contact.contactedElsewhereCampaignName?.trim();
    const base = campaignName
      ? t('contactedElsewhereInCampaign', { campaign: campaignName })
      : t('contactedElsewhere');

    // `contactedElsewhereReason` is deliberately not shown: production holds exactly one
    // value, `contacted_in_other_campaign`, which repeats the sentence above in machine
    // form. The date is the part that adds something — it is set on roughly a fifth of
    // these records, so it stays optional.
    const at = contact.contactedElsewhereAt ? new Date(contact.contactedElsewhereAt) : null;
    if (!at || Number.isNaN(at.getTime())) return base;
    return `${base} (${at.toLocaleDateString(i18n.language)})`;
  };

  const getStatusIcon = () => {
    if (contact.contactedElsewhere) {
      return (
        <PaiTooltip title={contactedElsewhereLabel()}>
          <MarkEmailReadIcon className={cx(classes.contactedElsewhereIcon, classes.statusIcon)} />
        </PaiTooltip>
      );
    }
    if (contact.isSuspended) {
      return (
        <PaiTooltip title={contact.suspensionReason ?? t('suspended')}>
          <WarningRoundedIcon className={cx(classes.warningIcon, classes.statusIcon)} />
        </PaiTooltip>
      );
    }
    if (contact.isBlacklisted) {
      return (
        <PaiTooltip title={`${t('blacklistSource')}: ${contact.blacklistName ?? t('unknown')}`}>
          <DoNotDisturbIcon className={cx(classes.blacklistedIcon, classes.statusIcon)} />
        </PaiTooltip>
      );
    }
    if (hasUncertainSex) {
      return (
        <PaiTooltip title={t('uncertainSex')}>
          <WcIcon className={cx(classes.genderIcon, classes.statusIcon)} />
        </PaiTooltip>
      );
    }
    if (!_.isEmpty(contact.isPresentInFunnels)) {
      return (
        <PaiTooltip title={`${t('presentIn')}: ${contact.isPresentInFunnels?.map((funnel) => funnel.funnelName).join(', ')}`}>
          <MoreIcon className={cx(classes.moreIcon, classes.statusIcon)} />
        </PaiTooltip>
      );
    }
    return null;
  };

  const renderCell = (columnId: string) => {
    switch (columnId) {
      case 'isActive':
        return (
          <TableCell
            data-contact-toggle=""
            // The row opens the contact drawer. A disabled MUI switch has
            // pointer-events: none, so the click landed on the row instead of
            // the control — exactly when the user was trying to flip it.
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-start'} gap={1}>
              {!deleteMultipleContacts ? (
                // A disabled switch swallows hover events, so the tooltip needs a live
                // wrapper — otherwise the explanation is unreachable exactly where the
                // question comes up.
                <PaiTooltip title={contact.contactedElsewhere && !contact.isActive ? contactedElsewhereLabel() : ''}>
                  <span>
                    <PaiSwitch
                      checked={!!contact.isActive}
                      onClick={handleSingleContactIsActiveChange(contact)}
                      disabled={
                        contactsLoading ||
                        disabled ||
                        (contact.isBlacklisted && !contact.isActive) ||
                        (contact.isSuspended && !contact.isActive) ||
                        (contact.isDeleted && !contact.isActive)
                      }
                    />
                  </span>
                </PaiTooltip>
              ) : (
                <PaiCheckbox checked={isSelectedForDeletion} onClick={handleContactsToDeleteChange(contact)} disabled={contactsLoading || disabled} />
              )}
              <Divider orientation={'vertical'} sx={{ height: '20px', marginLeft: '4px' }} />
              <IconButton
                className={classes.iconButton}
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleOpenMenu(event.target);
                }}
              >
                <MoreVertIcon />
              </IconButton>
            </Stack>
          </TableCell>
        );
      case 'name':
        return (
          <TableCell>
            <Stack direction={'row'} alignItems={'center'} gap={1} style={{ marginTop: '2px', position: 'relative' }}>
              {getStatusIcon()}
              <Highlight
                text={
                  contact.fullName?.trim() ||
                  (!_.isNil(contact.firstName) && !_.isNil(contact.lastName) ? `${contact.firstName} ${contact.lastName}` : t('linkedInContact'))
                }
                searchTerm={contactsSearchParam}
              />
              {!deleteMultipleContacts && contact.preprocessedProfileAnalysis && !contact.isSuspended ? (
                !contact.isReviewed ? (
                  <VisibilityIcon className={classes.icon} />
                ) : (
                  <VisibilityIcon className={classes.checkIcon} />
                )
              ) : null}
            </Stack>
          </TableCell>
        );
      case 'aiJudgement':
        return (
          <TableCell sx={(theme) => ({ color: contact.aiJudgement === Qualifications.ERROR ? theme.palette.warning.main : undefined })}>
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              {contact.aiJudgement === Qualifications.ERROR ? (
                <PaiHelpIcon tooltipText={contact.aiJudgementExplanation || false} variant={'warning'} />
              ) : null}
              {mapAiJudgementToText(contact.aiJudgement)}
            </Stack>
          </TableCell>
        );
      case 'company':
        return (
          <TableCell>
            {!_.isNil(contact.currentCompanies) && !_.isEmpty(contact.currentCompanies) ? (
              <CollapsibleText
                text={contact.currentCompanies.map((company) => (typeof company === 'string' ? company : company.companyName)).join(', ')}
                maxLength={40}
                searchTerm={contactsSearchParam}
              />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'jobTitle':
        return (
          <TableCell>
            {contact.occupation ? (
              <CollapsibleText text={contact.occupation} maxLength={40} searchTerm={contactsSearchParam} />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'location':
        return (
          <TableCell>
            {contact.location ? (
              <CollapsibleText text={contact.location} maxLength={40} searchTerm={contactsSearchParam} />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'email':
        return (
          <TableCell>
            {contact.email ? (
              <Highlight text={contact.email} searchTerm={contactsSearchParam} />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'searchName':
        return (
          <TableCell>
            {contact.searchName ? (
              <CollapsibleText text={contact.searchName} maxLength={40} searchTerm={contactsSearchParam} />
            ) : contact.fileName ? (
              <CollapsibleText text={contact.fileName} maxLength={40} searchTerm={contactsSearchParam} />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'customField':
        return (
          <TableCell>
            {contact.customField ? (
              <CollapsibleText text={contact.customField} maxLength={40} />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      case 'linkedInURL':
        return (
          <TableCell>
            {contact.linkedInURL ? (
              <PaiLink
                text={_.truncate(readableLinkedInURL ? readableLinkedInURL.replace(/^(https?:\/\/)?(www\.)?linkedin\.com/, '...') : '', {
                  length: 40,
                })}
                size={'md'}
                variant={'primary'}
                IconComponent={<OpenInNewIcon />}
                href={contact.linkedInURL}
                target={'_blank'}
              />
            ) : !contact.preprocessedProfileAnalysis ? (
              <NotYetVisited onOpenReasons={handleOpenNotVisitedDialog} />
            ) : (
              <DataNotAvailable />
            )}
          </TableCell>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <TableRow
        className={contactsLoading ? classes.loading : undefined}
        onClick={
          !deleteMultipleContacts && contact.preprocessedProfileAnalysis && !contact.isSuspended
            ? (event: React.MouseEvent) => {
                if ((event.target as HTMLElement).closest('[data-contact-toggle]')) return;
                handleOpenContactDrawer(contact.linkedInURL, contact.profileIds?.linkedin, funnelId)();
              }
            : undefined
        }
        hover={!deleteMultipleContacts && contact.preprocessedProfileAnalysis && !contact.isSuspended}
        selected={activeInDrawer}
        style={{ cursor: !deleteMultipleContacts && contact.preprocessedProfileAnalysis && !contact.isSuspended ? 'pointer' : undefined }}
      >
        {visibleColumnOrder.map((columnId) => (
          <React.Fragment key={columnId}>{renderCell(columnId)}</React.Fragment>
        ))}
      </TableRow>

      <NotYetVisitedDialog open={notVisitedDialogOpen} handleClose={handleCloseNotVisitedDialog} />
      <ContactItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        handleCloseMenu={handleCloseMenu}
        handleOpenDeleteContactPermanentlyDialog={handleOpenDeleteContactPermanentlyDialog(contact)}
        handleOpenChangeAiJudgementDialog={handleOpenChangeAiJudgementDialog(contact)}
        handleRemoveContactFromBlacklist={contact.isBlacklisted ? handleRemoveContactFromBlacklist(contact.linkedInURL) : undefined}
        handleRemoveContactFromSuspended={contact.isSuspended ? handleRemoveContactFromSuspended(contact.linkedInURL) : undefined}
        handleOpenUpdateSexDialog={hasUncertainSex ? handleOpenUpdateSexDialog(contact) : undefined}
        handleRecoverContactFromDeleted={contact.isDeleted ? handleRecoverContactFromDeleted(contact.linkedInURL) : undefined}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  loading: {
    pointerEvents: 'none',
    background: theme.palette.action.hover,
  },
  statusIcon: {
    position: 'absolute',
    left: '-32px',
  },
  blacklistedIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.error.main,
  },
  warningIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.warning.main,
  },
  moreIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
    transform: 'rotate(180deg)',
  },
  genderIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  contactedElsewhereIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.info.main,
  },
  notAvailableText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.light,
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.greys.light,
  },
  checkIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.success.main,
    opacity: 0.5,
  },
  iconButton: {
    padding: '4px',
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
    },
  },
}));

export default ContactItem;
